// Maps between the Current Loan Dashboard's per-pair state (src/pages/current-loan/dashboard)
// and the Encompass-backed `updateBorrowerPair`/`getBorrowerPair` JSON shape used by the
// Add New Loan wizard (src/components/loan-form/*). Every payload key below is the SAME key
// the matching wizard step sends, so a Dashboard save lands in Encompass exactly like a
// wizard save:
//   Step 1 edit-loan-application · Step 2 loan-address-application ·
//   Step 4 loan-employment-application · Step 5 loan-assets-application ·
//   Step 6 loan-demographic-application
//
// SAFETY RULE: the backend does a PARTIAL merge across top-level keys of the borrower-pair
// document (keys you omit are left untouched), but a FULL REPLACE within any key you DO
// include. Declarations/military/demographics have no key of their own — they are flatly
// merged into the same `borrower`/`coborrower` object as personal info — so any save that
// touches `borrower`/`coborrower` must resend the complete merged object.
//
// Employment, other income, assets/liabilities/REO and declarations are kept in local state
// in the wizard's own row shape (same keys, incl. Encompass `id`s), so they round-trip
// unchanged — nothing the wizard stores on a row is dropped by a Dashboard save.

import { toEncompassCitizenship, FOREIGN_NATIONAL } from "./foreign-national-fields";
import {
	CLOSING_VESTING_FIELD_IDS,
	CLOSING_ENTITY_NAME_READ_ALT_ID,
	INDIVIDUAL_VESTING_METHODS,
	ENTITY_VESTING_METHODS,
} from "./current-loan-encompass-field-ids";

const isBlank = (v) => v === null || v === undefined || v === "";

/** Accepts the wizard's booleans plus legacy Dashboard "Yes"/"No" strings. */
export const toBool = (v) => {
	if (v === true || v === "true" || v === "Yes" || v === "Y") return true;
	if (v === false || v === "false" || v === "No" || v === "N") return false;
	return null;
};

const toYN = (v) => {
	if (v === "Y" || v === "Yes" || v === true || v === "true") return "Y";
	if (v === "N" || v === "No" || v === false || v === "false") return "N";
	return "";
};

// MM/DD/YYYY (as typed on the Dashboard) or ISO → MM-dd-yyyy (the wizard's saved date format).
export const toWizardDate = (value) => {
	const mdy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value || "");
	if (mdy) return `${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}-${mdy[3]}`;
	const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
	if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;
	return value || "";
};

// Any typed SSN → ###-##-#### (SSNMaskedInput's saved format); partial input is left as is.
export const toWizardSSN = (value) => {
	const digits = String(value || "").replace(/\D/g, "");
	return digits.length === 9
		? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
		: value || "";
};

// ISO / MM-dd-yyyy (as stored) → MM/DD/YYYY (as shown on the Dashboard).
const fromWizardDate = (value) => {
	if (!value) return "";
	const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
	const mdy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
	if (mdy) return `${mdy[1]}/${mdy[2]}/${mdy[3]}`;
	return value;
};

// ── Step 6: Declarations / Military / Demographics (per person, wizard keys) ─────────────

// Yes/No declaration questions A–M, in wizard order (declaration-information/index.jsx).
export const DECLARATION_QUESTION_KEYS = [
	"intentToOccupyIndicator",
	"specialBorrowerSellerRelationshipIndicator",
	"undisclosedBorrowedFundsIndicator",
	"undisclosedMortgageApplicationIndicator",
	"undisclosedCreditApplicationIndicator",
	"propertyProposedCleanEnergyLienIndicator",
	"undisclosedComakerOfNoteIndicator",
	"outstandingJudgementsIndicator",
	"presentlyDelinquentIndicatorURLA",
	"partyToLawsuitIndicatorURLA",
	"priorPropertyDeedInLieuConveyedIndicator",
	"priorPropertyShortSaleCompletedIndicator",
	"priorPropertyForeclosureCompletedIndicator",
	"bankruptcyIndicator",
];

const DECLARATION_BOOLEAN_KEYS = [...DECLARATION_QUESTION_KEYS, "homeownerPastThreeYearsIndicator"];

export const BANKRUPTCY_CHAPTER_KEYS = [
	"bankruptcyIndicatorChapterSeven",
	"bankruptcyIndicatorChapterEleven",
	"bankruptcyIndicatorChapterTwelve",
	"bankruptcyIndicatorChapterThirteen",
];

export const MILITARY_CHECKBOX_KEYS = [
	"activeDuty",
	"veteran",
	"reserveNationalGuardReserveActivated",
	"spousalVABenefitsEligibilityIndicator",
];

export const VISUAL_OBSERVATION_KEYS = [
	"isEthnicityBasedOnVisual",
	"isRaceBasedOnVisual",
	"isSexBasedOnVisual",
];

export const ETHNICITY_KEYS = [
	"hmdaEthnicityHispanicLatinoIndicator",
	"hmdaEthnicityNotHispanicLatinoIndicator",
	"hmdaEthnicityDoNotWishIndicator",
];
export const RACE_KEYS = [
	"hmdaAmericanIndianIndicator",
	"hmdaAsianIndicator",
	"hmdaAfricanAmericanIndicator",
	"hmdaPacificIslanderIndicator",
	"hmdaWhiteIndicator",
	"hmdaRaceDoNotWishProvideIndicator",
];
export const SEX_KEYS = [
	"hmdaGendertypeFemaleIndicator",
	"hmdaGendertypeMaleIndicator",
	"hmdaGendertypeDoNotWishIndicator",
];
// The wizard writes and reads the sex answers under both casings (demographic-information).
const upperSexKey = (key) => `H${key.slice(1)}`;
const DEMOGRAPHIC_CHECKBOX_KEYS = [
	...ETHNICITY_KEYS,
	"hmdaMexicanIndicator",
	"hmdaPuertoRicanIndicator",
	"hmdaCubanIndicator",
	"hmdaHispanicLatinoOtherOriginIndicator",
	...RACE_KEYS,
	"hmdaAsianIndianIndicator",
	"hmdaChineseIndicator",
	"hmdaFilipinoIndicator",
	"hmdaJapaneseIndicator",
	"hmdaKoreanIndicator",
	"hmdaVietnameseIndicator",
	"hmdaAsianOtherRaceIndicator",
	"hmdaNativeHawaiianIndicator",
	"hmdaGuamanianOrChamorroIndicator",
	"hmdaSamoanIndicator",
	"hmdaPacificIslanderOtherIndicator",
	...SEX_KEYS,
];
const DEMOGRAPHIC_TEXT_KEYS = [
	"hmdaOtherHispanicLatinoOrigin",
	"hmdaAmericanIndianTribe",
	"hmdaOtherAsianRace",
	"hmdaOtherPacificIslanderRace",
];

/** Unanswered declarations/military/demographics for one person. */
export const emptyDeclarations = () => {
	const d = {};
	DECLARATION_BOOLEAN_KEYS.forEach((k) => {
		d[k] = null;
	});
	BANKRUPTCY_CHAPTER_KEYS.forEach((k) => {
		d[k] = false;
	});
	d.priorPropertyUsageType = "";
	d.priorPropertyTitleType = "";
	d.undisclosedBorrowedFundsAmount = "";
	d.selfDeclaredMilitaryServiceIndicator = null;
	MILITARY_CHECKBOX_KEYS.forEach((k) => {
		d[k] = false;
	});
	d.militaryServiceExpectedCompletionDate = "";
	d.applicationTakenMethodType = "";
	VISUAL_OBSERVATION_KEYS.forEach((k) => {
		d[k] = "";
	});
	DEMOGRAPHIC_CHECKBOX_KEYS.forEach((k) => {
		d[k] = false;
	});
	DEMOGRAPHIC_TEXT_KEYS.forEach((k) => {
		d[k] = "";
	});
	return d;
};

const mapDeclarationsFromReal = (real) => {
	const d = emptyDeclarations();
	DECLARATION_BOOLEAN_KEYS.forEach((k) => {
		d[k] = toBool(real[k]);
	});
	BANKRUPTCY_CHAPTER_KEYS.forEach((k) => {
		d[k] = toBool(real[k]) === true;
	});
	d.priorPropertyUsageType = real.priorPropertyUsageType || "";
	d.priorPropertyTitleType = real.priorPropertyTitleType || "";
	d.undisclosedBorrowedFundsAmount = isBlank(real.undisclosedBorrowedFundsAmount)
		? ""
		: String(real.undisclosedBorrowedFundsAmount);
	MILITARY_CHECKBOX_KEYS.forEach((k) => {
		d[k] = toBool(real[k]) === true;
	});
	const served = toBool(real.selfDeclaredMilitaryServiceIndicator);
	d.selfDeclaredMilitaryServiceIndicator =
		served !== null ? served : MILITARY_CHECKBOX_KEYS.some((k) => d[k]) ? true : null;
	d.militaryServiceExpectedCompletionDate = fromWizardDate(real.militaryServiceExpectedCompletionDate);
	d.applicationTakenMethodType = real.applicationTakenMethodType || "";
	VISUAL_OBSERVATION_KEYS.forEach((k) => {
		d[k] = toYN(real[k]);
	});
	DEMOGRAPHIC_CHECKBOX_KEYS.forEach((k) => {
		d[k] = toBool(real[k]) === true;
	});
	SEX_KEYS.forEach((k) => {
		if (real[k] === undefined && real[upperSexKey(k)] !== undefined) {
			d[k] = toBool(real[upperSexKey(k)]) === true;
		}
	});
	DEMOGRAPHIC_TEXT_KEYS.forEach((k) => {
		d[k] = real[k] || "";
	});
	if (!d.hmdaAmericanIndianTribe && real.americanIndianOtherValue) {
		d.hmdaAmericanIndianTribe = real.americanIndianOtherValue;
	}
	return d;
};

const buildDeclarationsPayload = (decl) => {
	const d = { ...emptyDeclarations(), ...(decl || {}) };
	const out = {};
	DECLARATION_BOOLEAN_KEYS.forEach((k) => {
		if (d[k] !== null && d[k] !== undefined) out[k] = d[k];
	});
	BANKRUPTCY_CHAPTER_KEYS.forEach((k) => {
		out[k] = !!d[k];
	});
	out.priorPropertyUsageType = d.priorPropertyUsageType || "";
	out.priorPropertyTitleType = d.priorPropertyTitleType || "";
	out.undisclosedBorrowedFundsAmount = d.undisclosedBorrowedFundsAmount || "";
	// The wizard's military radio stores "true" / "false" strings (military-information).
	const served = toBool(d.selfDeclaredMilitaryServiceIndicator);
	if (served !== null) out.selfDeclaredMilitaryServiceIndicator = String(served);
	MILITARY_CHECKBOX_KEYS.forEach((k) => {
		out[k] = !!d[k];
	});
	out.militaryServiceExpectedCompletionDate = toWizardDate(d.militaryServiceExpectedCompletionDate);
	out.applicationTakenMethodType = d.applicationTakenMethodType || "";
	VISUAL_OBSERVATION_KEYS.forEach((k) => {
		out[k] = d[k] || "";
	});
	DEMOGRAPHIC_CHECKBOX_KEYS.forEach((k) => {
		out[k] = !!d[k];
	});
	SEX_KEYS.forEach((k) => {
		out[upperSexKey(k)] = !!d[k];
	});
	DEMOGRAPHIC_TEXT_KEYS.forEach((k) => {
		out[k] = d[k] || "";
	});
	out.americanIndianOtherValue = d.hmdaAmericanIndianTribe || "";
	return out;
};

// ── Step 1: Borrower / co-borrower personal info (+ Step 6 flattened in) ────────────────

/**
 * Full `borrower`/`coborrower` object for updateBorrowerPair — personal info (Step 1) merged
 * with that person's own declarations/military/demographics (Step 6), as the wizard sends it.
 * @param {object} person Dashboard person state
 * @param {object} decl that person's declarations (wizard keys)
 * @param {{mailingIsSame?: boolean, hasCoBorrower?: boolean}} [extra] address flags stored on
 *   the person object, and whether the pair has a co-borrower
 */
export const buildPersonPayload = (person, decl, extra = {}) => {
	const p = person || {};
	// Value types match the wizard: legalOtherThanSpouse is a boolean, dependentCount a
	// number (only sent once entered), birthDate MM-dd-yyyy and the SSN ###-##-####.
	const legal = p.maritalStatus === "Unmarried" ? toBool(p.legalOtherThanSpouse) : null;
	const payload = {
		firstName: p.firstName || "",
		middleName: p.middleName || "",
		lastName: p.lastName || "",
		birthDate: toWizardDate(p.dob),
		maritalStatusType: p.maritalStatus || "",
		...(legal !== null ? { legalOtherThanSpouse: legal } : {}),
		domesticRelationshipType: p.maritalStatus === "Unmarried" ? p.domesticRelationshipType || "" : "",
		urla2020CitizenshipResidencyType: toEncompassCitizenship(p.citizenship),
		urla2020CountryOfCitizenship:
			p.citizenship === "NonPermanentResidentAlien" || p.citizenship === FOREIGN_NATIONAL
				? p.countryOfCitizenship || ""
				: "",
		taxIdentificationIdentifier: toWizardSSN(p.ssn),
		mobilePhone: p.cellPhone || "",
		workPhoneNumber: p.workPhone || "",
		homePhoneNumber: p.homePhone || "",
		emailAddressText: p.email || "",
		...(isBlank(p.dependents) ? {} : { dependentCount: Number(p.dependents) }),
		dependentsAgesDescription: p.dependentAges || "",
		...buildDeclarationsPayload(decl),
	};
	// The wizard only sets this once a co-borrower exists (borrower-information:216-224).
	if (extra.hasCoBorrower) payload.hmdaNoCoApplicantIndicator = false;
	if (extra.mailingIsSame !== undefined) {
		payload.mailingAddressSameAsPresentIndicator = !!extra.mailingIsSame;
	}
	return payload;
};

/** Reverse of buildPersonPayload — hydrates a Dashboard person + that person's declarations
 * from a real borrower/coborrower object returned by getBorrowerPair.
 * @param {object} real
 * @param {boolean} [isForeignNational] CX.FOREIGNNATLBORRn === "Yes" for this slot */
export const mapPersonFromReal = (real, isForeignNational = false) => {
	if (!real) return null;
	const citizenship = real.urla2020CitizenshipResidencyType || "";
	return {
		borrower: {
			firstName: real.firstName || "",
			middleName: real.middleName || "",
			lastName: real.lastName || "",
			dob: fromWizardDate(real.birthDate),
			maritalStatus: real.maritalStatusType || "",
			legalOtherThanSpouse: isBlank(real.legalOtherThanSpouse)
				? ""
				: String(toBool(real.legalOtherThanSpouse) ?? ""),
			domesticRelationshipType: real.domesticRelationshipType || "",
			citizenship:
				isForeignNational && citizenship === "NonPermanentResidentAlien" ? FOREIGN_NATIONAL : citizenship,
			countryOfCitizenship: real.urla2020CountryOfCitizenship || "",
			ssn: real.taxIdentificationIdentifier || "",
			cellPhone: real.mobilePhone || "",
			workPhone: real.workPhoneNumber || "",
			homePhone: real.homePhoneNumber || "",
			email: real.emailAddressText || "",
			dependents: real.dependentCount != null ? String(real.dependentCount) : "",
			dependentAges: real.dependentsAgesDescription || "",
		},
		decl: mapDeclarationsFromReal(real),
		mailingIsSame:
			real.mailingAddressSameAsPresentIndicator === undefined
				? undefined
				: toBool(real.mailingAddressSameAsPresentIndicator) === true,
	};
};

// ── Step 1: Subject property (loan level) ────────────────────────────────────────────────

/** updateLoan loanData for the Step 1 "Subject Property Details" card — same keys as
 * edit-loan-application/index.jsx:1322-1341. `existingFees` is getLoan().fees, merged the same
 * way the wizard does so other fee entries are never dropped. */
export const buildSubjectPropertyLoanData = (property, existingFees) => {
	const pr = property || {};
	let fees = [];
	if (!existingFees || existingFees.length === 0) {
		fees = [
			{ feeType: "FloodInsurance", monthlyPayment: pr.floodInsurance || "" },
			{ feeType: "PropertyTaxes", monthlyPayment: pr.propertyTaxes || "" },
		];
	} else {
		fees = existingFees.map((fee) => {
			if (fee.feeType === "FloodInsurance") return { ...fee, monthlyPayment: pr.floodInsurance || "" };
			if (fee.feeType === "PropertyTaxes") return { ...fee, monthlyPayment: pr.propertyTaxes || "" };
			return fee;
		});
		if (!fees.some((f) => f.feeType === "FloodInsurance")) {
			fees.push({ feeType: "FloodInsurance", monthlyPayment: pr.floodInsurance || "" });
		}
		if (!fees.some((f) => f.feeType === "PropertyTaxes")) {
			fees.push({ feeType: "PropertyTaxes", monthlyPayment: pr.propertyTaxes || "" });
		}
	}
	return {
		property: {
			addressLineText: pr.street || "",
			addressUnitIdentifier: pr.unit || "",
			city: pr.city || "",
			state: pr.state || "",
			postalCode: pr.zip || "",
			financedNumberOfUnits: pr.units || "",
		},
		subjectPropertyGrossRentalIncomeAmount: pr.grossRent ?? "",
		proposedHazardInsuranceAmount: pr.homeownersInsurance || "",
		proposedDuesAmount: pr.hoaDues || "",
		fees,
		proposedRealEstateTaxesAmount:
			fees.find((f) => f.feeType === "PropertyTaxes")?.monthlyPayment || "0",
	};
};

/** Reverse of buildSubjectPropertyLoanData from a getLoan() response. Only returns keys
 * Encompass actually has a value for, so it can be merged over local state. */
export const mapSubjectPropertyFromLoan = (resp) => {
	if (!resp) return {};
	const out = {};
	const set = (key, value) => {
		if (!isBlank(value)) out[key] = String(value);
	};
	const prop = resp.property || {};
	set("street", prop.addressLineText);
	set("unit", prop.addressUnitIdentifier || prop.unitNumber);
	set("city", prop.city);
	set("state", prop.state);
	set("zip", prop.postalCode);
	set("units", prop.financedNumberOfUnits);
	set("grossRent", resp.subjectPropertyGrossRentalIncomeAmount);
	set("homeownersInsurance", resp.proposedHazardInsuranceAmount);
	set("hoaDues", resp.proposedDuesAmount);
	const fees = resp.fees || [];
	set("floodInsurance", fees.find((f) => f.feeType === "FloodInsurance")?.monthlyPayment);
	set(
		"propertyTaxes",
		fees.find((f) => f.feeType === "PropertyTaxes")?.monthlyPayment ?? resp.proposedRealEstateTaxesAmount
	);
	return out;
};

// ── Step 2: Residences — same 6-slot layout as loan-address-application ──────────────────
// Slots per pair: 0 Borrower Current, 1 Borrower Prior, 2 Borrower Mailing,
//                 3 CoBorrower Current, 4 CoBorrower Prior, 5 CoBorrower Mailing.

const addressPart = (addr, prefix) => {
	const key = (k) => (prefix ? `${prefix}${k[0].toUpperCase()}${k.slice(1)}` : k);
	return {
		uRLA2020StreetAddress: addr[key("street")] || "",
		addressUnitIdentifier: addr[key("unit")] || "",
		addressCity: addr[key("city")] || "",
		addressState: addr[key("state")] || "",
		addressPostalCode: addr[key("zip")] || "",
		foreignAddressIndicator: !!addr[key("foreignAddressIndicator")],
		country: addr[key("country")] || "",
	};
};

const buildResidenceEntries = (addr, applicantType, slotOffset) => {
	const a = addr || {};
	const ids = a.residenceIds || {};
	const current = {
		id: ids.current || `Residence/${slotOffset}`,
		applicantType,
		residencyType: "Current",
		mailingAddressIndicator: false,
		...addressPart(a, ""),
		residencyBasisType: a.residencyBasisType || "",
		rent: a.residencyBasisType === "Rent" ? a.rent || "" : "",
		durationTermYears: a.durationYears ?? "",
		durationTermMonths: a.durationMonths ?? "",
	};
	const prior = {
		id: ids.prior || `Residence/${slotOffset + 1}`,
		applicantType,
		residencyType: "Prior",
		mailingAddressIndicator: false,
		...addressPart(a, "prior"),
	};
	// "Same as current" copies the current address into the mailing slot, as the wizard does.
	const mailing = {
		id: ids.mailing || `Residence/${slotOffset + 2}`,
		applicantType,
		mailingAddressIndicator: true,
		...(a.mailingIsSame ? addressPart(a, "") : addressPart(a, "mailing")),
	};
	return [current, prior, mailing];
};

export const buildResidencesPayload = (bAddr, cbAddr, hasCoBorrower) => {
	const residences = buildResidenceEntries(bAddr, "Borrower", 0);
	if (hasCoBorrower) residences.push(...buildResidenceEntries(cbAddr, "CoBorrower", 3));
	return residences;
};

const MAILING_PARTS = ["street", "unit", "city", "state", "zip", "foreignAddressIndicator", "country"];
const mailingKey = (part) => `mailing${part[0].toUpperCase()}${part.slice(1)}`;

/** Step 2 "Same as current information" (contact-information handleSameAddress): checking it
 * copies the current address into the mailing slot; unchecking blanks the copied fields. */
export const applyMailingSameAsCurrent = (addr, same) => {
	const a = { ...(addr || {}), mailingIsSame: !!same };
	MAILING_PARTS.forEach((part) => {
		const blank = part === "foreignAddressIndicator" ? false : "";
		a[mailingKey(part)] = same ? a[part] ?? blank : blank;
	});
	return a;
};

/** Reverse of buildResidencesPayload — hydrates bAddr/cbAddr from a getBorrowerPair
 * `residences` array. */
export const mapResidencesFromReal = (residences) => {
	const list = residences || [];
	const fromEntry = (entry, prefix) => {
		const key = (k) => (prefix ? `${prefix}${k[0].toUpperCase()}${k.slice(1)}` : k);
		return {
			[key("street")]: entry.uRLA2020StreetAddress || "",
			[key("unit")]: entry.addressUnitIdentifier || "",
			[key("city")]: entry.addressCity || "",
			[key("state")]: entry.addressState || "",
			[key("zip")]: entry.addressPostalCode || "",
			[key("foreignAddressIndicator")]: toBool(entry.foreignAddressIndicator) === true,
			[key("country")]: entry.country || "",
		};
	};
	const forType = (applicantType) => {
		const mine = list.filter((r) => r.applicantType === applicantType);
		const current = mine.find((r) => r.residencyType === "Current" && !r.mailingAddressIndicator) || {};
		const prior = mine.find((r) => r.residencyType === "Prior") || {};
		const mailing = mine.find((r) => r.mailingAddressIndicator) || {};
		return {
			...fromEntry(current, ""),
			residencyBasisType: current.residencyBasisType || "",
			rent: isBlank(current.rent) ? "" : String(current.rent),
			durationYears: isBlank(current.durationTermYears) ? "" : String(current.durationTermYears),
			durationMonths: isBlank(current.durationTermMonths) ? "" : String(current.durationTermMonths),
			...fromEntry(prior, "prior"),
			...fromEntry(mailing, "mailing"),
			residenceIds: { current: current.id, prior: prior.id, mailing: mailing.id },
		};
	};
	return {
		bAddr: forType("Borrower"),
		cbAddr: forType("CoBorrower"),
		hasAny: list.length > 0,
	};
};

// ── Step 4: Employment & Income (wizard row shape) ───────────────────────────────────────

const employmentIdNumber = (row) => parseInt(String(row?.id || "").split("/")[1], 10);

/** New employment row, same defaults as borrower-employment-information/index.jsx:24-86.
 * The id follows the wizard's formula; if that id is already taken (the wizard can collide
 * once rows have been reordered) the next free number is used instead. */
export const newEmploymentRow = (rows, owner, current) => {
	const list = rows || [];
	const ownerCount = list.filter((r) => r.owner === owner).length;
	let idNumber = ownerCount === 0 ? 1 : list.length;
	const taken = new Set(list.map(employmentIdNumber).filter((n) => Number.isFinite(n)));
	while (taken.has(idNumber)) idNumber += 1;
	return {
		id: `Employment/${idNumber}`,
		owner,
		currentEmploymentIndicator: current,
		noLinkToDocTrackIndicator: true,
		militaryEmployer: false,
	};
};

/** Default rows loan-employment-application starts a pair with (index.jsx:192-203). */
export const defaultEmploymentRows = () => [
	{ id: "Employment/0", owner: "Borrower", currentEmploymentIndicator: true },
	{ id: "Employment/1", owner: "CoBorrower", currentEmploymentIndicator: true },
];

/** loan-employment-application reorderFinanceArray: fill a missing owner, interleave
 * borrower / co-borrower rows, give rows without an id one, then sort by id. */
export const reorderEmploymentRows = (rows) => {
	const borrowers = (rows || []).filter((r) => r.owner === "Borrower");
	const coBorrowers = (rows || []).filter((r) => r.owner === "CoBorrower");
	if (borrowers.length === 0) borrowers.push({ owner: "Borrower", currentEmploymentIndicator: true });
	if (coBorrowers.length === 0) coBorrowers.push({ owner: "CoBorrower", currentEmploymentIndicator: true });

	const longer = borrowers.length > coBorrowers.length ? borrowers : coBorrowers;
	const merged = longer.reduce(
		(acc, cur, i) =>
			borrowers[i] && coBorrowers[i] ? [...acc, borrowers[i], coBorrowers[i]] : [...acc, cur],
		[]
	);
	const withIds = merged.map((row, index) => (row.id ? { ...row } : { ...row, id: `Employment/${index}` }));
	return withIds.sort((a, b) => employmentIdNumber(a) - employmentIdNumber(b));
};

export const isSelfEmployedRow = (row) =>
	row?.selfEmployedIndicator === true || row?.selfEmployedIndicator === "true";

export const buildEmploymentPayload = (employmentRows, otherIncomeRows, hasCoBorrower) => ({
	employment: (employmentRows || [])
		.filter((row) => hasCoBorrower || row.owner !== "CoBorrower")
		.map((row) => {
			const out = {
				...row,
				// the wizard writes the street to both keys (employment-information/index.jsx:456)
				addressStreetLine1: row.uRLA2020StreetAddress || row.addressStreetLine1 || "",
			};
			// the wizard's radio stores "true" / "false"; dates are saved as MM-dd-yyyy
			if (row.selfEmployedIndicator !== undefined && row.selfEmployedIndicator !== "") {
				out.selfEmployedIndicator = String(isSelfEmployedRow(row));
			}
			if (row.employmentStartDate) out.employmentStartDate = toWizardDate(row.employmentStartDate);
			if (row.endDate) out.endDate = toWizardDate(row.endDate);
			return out;
		}),
	otherIncomeSources: (otherIncomeRows || []).filter(
		(row) => hasCoBorrower || row.owner !== "CoBorrower"
	),
});

export const mapEmploymentFromReal = (employment, otherIncomeSources) => ({
	employment: (employment || []).length
		? reorderEmploymentRows(employment).map((row) => ({
				...row,
				uRLA2020StreetAddress: row.uRLA2020StreetAddress || row.addressStreetLine1 || "",
				// getBorrowerPair returns ISO dates; the fields show MM-DD-YYYY.
				employmentStartDate: toWizardDate(row.employmentStartDate),
				endDate: toWizardDate(row.endDate),
		  }))
		: [],
	otherIncome: otherIncomeSources || [],
});

// ── Step 5: Assets & Liabilities (wizard row shape) ──────────────────────────────────────

export const emptyAssets = () => ({
	assets: [],
	otherAssets: [],
	liabilities: [],
	otherLiabilities: [],
	reoProperties: [],
});

/** REO key a liability's `reoId` points at — new rows carry `reoId`, saved rows `altId`. */
export const reoLinkKey = (reo) => reo?.reoId || reo?.altId || "";

// ── CLEARpath liabilities review (PathA-2.06 / 2.09 / 2.14) ─────────────────────────────

/** Source tag values the backend attaches to each liability (`?include_source=true`). */
export const LIABILITY_SOURCE = { CREDIT: "credit", CLIENT: "client", APPLICATION: "1003" };

export const LIABILITY_SOURCE_LABELS = {
	[LIABILITY_SOURCE.APPLICATION]: "1003",
	[LIABILITY_SOURCE.CREDIT]: "Credit Report",
	[LIABILITY_SOURCE.CLIENT]: "Added by you",
};

/** Source of a liability row: rows not yet saved are the client's; unknown → 1003. */
export const liabilitySource = (row) => {
	if (!row?.id && !row?.requestId) return LIABILITY_SOURCE.CLIENT;
	return row?.source || LIABILITY_SOURCE.APPLICATION;
};

/** UI-only keys kept on a liability row in the Dashboard — never sent to Encompass. */
export const LIABILITY_UI_KEYS = ["source", "removed", "snapshot", "isEdited"];

/** Production-editable liability keys (Add New Loan grid); a change to any of them on a
 * credit-report row marks it Edited (D6). `reoId` covers a REO link change. */
export const LIABILITY_EDIT_KEYS = [
	"owner",
	"liabilityType",
	"holderName",
	"accountIdentifier",
	"unpaidBalanceAmount",
	"payoffStatusIndicator",
	"monthlyPaymentAmount",
	"exclusionIndicator",
	"subjectLoanResubordinationIndicator",
	"bankLiabilityConsideredIndicator",
	"currentLienPosition",
	"proposedLienPosition",
	"reoId",
];

const normalizeLiabilityValue = (value) => {
	if (value === undefined || value === null || typeof value === "boolean") return !!value;
	const text = String(value).replace(/[$,]/g, "").trim();
	if (text === "") return false;
	if (text.toLowerCase() === "true" || text.toLowerCase() === "false") return text.toLowerCase() === "true";
	const number = Number(text);
	return Number.isNaN(number) ? text : Math.round(number * 100) / 100;
};

/** The editable fields of a liability as loaded — kept on credit rows as `snapshot`. */
export const liabilitySnapshot = (row) =>
	Object.fromEntries(LIABILITY_EDIT_KEYS.map((key) => [key, row?.[key] ?? ""]));

/** D6: a credit-report row differs from its loaded snapshot on any editable key. */
export const isCreditRowEdited = (row) =>
	liabilitySource(row) === LIABILITY_SOURCE.CREDIT &&
	!!row?.snapshot &&
	LIABILITY_EDIT_KEYS.some(
		(key) => normalizeLiabilityValue(row.snapshot[key]) !== normalizeLiabilityValue(row[key])
	);

/** Banner figure (PathA-2.06): monthly amount of rows that are neither removed nor
 * excluded from DTI. */
export const includedMonthlyDebt = (rows) =>
	(rows || []).reduce((sum, row) => {
		if (row?.removed || toBool(row?.exclusionIndicator)) return sum;
		const amount = Number(String(row?.monthlyPaymentAmount ?? "").replace(/[$,]/g, ""));
		return Number.isFinite(amount) ? sum + amount : sum;
	}, 0);

/** Read-back: credit rows carry a snapshot of their loaded values for the Edited diff. */
export const withLiabilitySnapshots = (rows) =>
	(rows || []).map((row) =>
		row?.source === LIABILITY_SOURCE.CREDIT ? { ...row, snapshot: liabilitySnapshot(row) } : row
	);

const stripLiabilityUiKeys = (row) =>
	Object.fromEntries(Object.entries(row || {}).filter(([key]) => !LIABILITY_UI_KEYS.includes(key)));

/** Saved liability rows staged for removal — deleted in Encompass on Save (D2). */
export const stagedLiabilityRemovals = (rows) => (rows || []).filter((row) => row?.removed && row?.id);

/** Encompass VOL ids for staged removals, resolved from getLoanDetail (matched on
 * requestId = altId, like resolveAssetDeleteIds). */
export const resolveLiabilityRemovalIds = (loanDetail, pairId, pairIndex, rows) => {
	const ids = [];
	let applicationId;
	let missing = 0;
	stagedLiabilityRemovals(rows).forEach((row) => {
		const resolved = resolveAssetDeleteIds(loanDetail, pairId, pairIndex, "liabilities", row);
		applicationId = applicationId || resolved.applicationId;
		if (resolved.itemId) ids.push(resolved.itemId);
		else missing += 1;
	});
	return { applicationId, ids, missing };
};

export const buildAssetsPayload = (assets) => {
	const a = { ...emptyAssets(), ...(assets || {}) };
	return {
		assets: a.assets,
		otherAssets: a.otherAssets,
		// Staged removals are deleted separately (deleteVox) before this PATCH.
		liabilities: (a.liabilities || []).filter((row) => !row?.removed).map(stripLiabilityUiKeys),
		otherLiabilities: a.otherLiabilities,
		reoProperties: a.reoProperties,
	};
};

// ── Step 10: Vesting read-back ────────────────────────────────────────────────────────────

/** Vesting from the Step 10 field() read. `get(id)` returns a field's value, or undefined
 * when blank. The wizard blanks field 33 on every entity save but never clears the entity
 * fields, so a filled 33 holding an individual method means individual vesting is current;
 * only otherwise is the entity pair used, so a stale entity value never wins.
 * @returns {{vestingType?: string, vestingWrittenAs?: string}} */
export const resolveClosingVesting = (get) => {
	const out = {};
	const individual = get(CLOSING_VESTING_FIELD_IDS.individual.vestingType);
	const entity = get(CLOSING_VESTING_FIELD_IDS.entity.vestingType);
	if (individual !== undefined && INDIVIDUAL_VESTING_METHODS.has(individual)) {
		out.vestingType = individual;
		const writtenAs = get(CLOSING_VESTING_FIELD_IDS.individual.vestingWrittenAs);
		if (writtenAs !== undefined) out.vestingWrittenAs = writtenAs;
	} else if (entity !== undefined && ENTITY_VESTING_METHODS.has(entity)) {
		out.vestingType = entity;
		const writtenAs =
			get(CLOSING_VESTING_FIELD_IDS.entity.vestingWrittenAs) ?? get(CLOSING_ENTITY_NAME_READ_ALT_ID);
		if (writtenAs !== undefined) out.vestingWrittenAs = writtenAs;
	}
	return out;
};

// ── Step 5 row helpers ───────────────────────────────────────────────────────────────────

export const MAX_ASSET_ROWS = 25; // every Step 5 card disables "+" at 25 rows

// Step 5 list → the index key its card numbers new rows with, and the deleteVox `type` /
// getLoanDetail application key its saved rows are deleted under (loan-assets-application).
export const ASSET_LIST_META = {
	assets: { indexKey: "vodIndex", encompassIndexKey: "assetIndex", deleteType: "vods" },
	otherAssets: { indexKey: "vodIndex", deleteType: "otherAssets" },
	liabilities: { indexKey: "volIndex", encompassIndexKey: "liabilityIndex", deleteType: "vols" },
	otherLiabilities: { indexKey: "volIndex", deleteType: "otherLiabilities" },
	reoProperties: {},
};

/** New Step 5 row, same defaults as the wizard's cards (assets-information,
 * other-assets-information, liabilities-information, other-liabilities-information,
 * real-estate). `makeId` supplies the REO row's uuid. */
export const newAssetRow = (listKey, rows, makeId) => {
	const list = rows || [];
	const { indexKey } = ASSET_LIST_META[listKey] || {};
	if (listKey === "reoProperties") return { reoId: makeId() };
	const next = list.length === 0 ? 1 : (Number(list[list.length - 1]?.[indexKey]) || list.length) + 1;
	const row = { [indexKey]: next };
	if (indexKey === "vodIndex") row.isVod = true;
	return row;
};

/** Removes a row that was never saved, renumbering the rows after it (handleDeleteTempRow). */
export const removeUnsavedAssetRow = (listKey, rows, idx) => {
	const list = rows || [];
	const { indexKey } = ASSET_LIST_META[listKey] || {};
	const removed = list[idx];
	const remaining = list.filter((_, i) => i !== idx);
	if (!indexKey || removed?.[indexKey] === undefined) return remaining;
	return remaining.map((row) =>
		row[indexKey] > removed[indexKey] ? { ...row, [indexKey]: row[indexKey] - 1 } : row
	);
};

/** After a saved row is deleted in Encompass, the rows after it take the previous row's
 * Encompass id and indexes, as loan-assets-application handleSubmitDelete does. */
export const reshiftAfterAssetDelete = (listKey, rows, idx) => {
	const list = rows || [];
	const { indexKey, encompassIndexKey } = ASSET_LIST_META[listKey] || {};
	if (listKey === "reoProperties") return list.filter((_, i) => i !== idx);
	return list
		.filter((_, i) => i !== idx)
		.map((row, i) => {
			if (i < idx) return row;
			const previous = list[i];
			const out = { ...row, id: previous?.id };
			if (encompassIndexKey) {
				out[encompassIndexKey] = previous?.[encompassIndexKey];
				out[indexKey] = previous?.[indexKey];
			}
			return out;
		});
};

/** Resolves the Encompass ids a delete needs from getLoanDetail's `applications`, the way the
 * wizard does: rows are matched on altId (liabilities on requestId), REO on altId.
 * @returns {{itemId?: string, applicationId?: string}} */
export const resolveAssetDeleteIds = (loanDetail, pairId, pairIndex, listKey, row) => {
	const applications = loanDetail?.applications || [];
	const application =
		applications.find((app) => app?.id && String(app.id) === String(pairId)) ||
		applications[pairIndex];
	if (!application) return {};
	if (listKey === "reoProperties") {
		const altId = row?.altId || row?.reoId;
		return {
			applicationId: application.id,
			itemId: (application.reoProperties || []).find((r) => r?.altId === altId)?.id,
		};
	}
	const { deleteType } = ASSET_LIST_META[listKey] || {};
	const matchKey = listKey === "liabilities" ? row?.requestId : row?.altId;
	const item = (application[deleteType] || []).find((r) => r?.altId === matchKey);
	return { applicationId: application.id, itemId: item?.id };
};

export const mapAssetsFromReal = (real) => ({
	assets: real?.assets || [],
	otherAssets: real?.otherAssets || [],
	liabilities: withLiabilitySnapshots(real?.liabilities),
	otherLiabilities: real?.otherLiabilities || [],
	reoProperties: (real?.reoProperties || []).map((r) => ({ ...r, reoId: reoLinkKey(r) })),
});
